import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

export interface TransferBlocker {
  code: string;
  message: string;
}

export interface TransferCompanyOption {
  id: number;
  name: string;
  type?: string;
}

export interface TransferHistoryEntry {
  id: number;
  companyId: number;
  companyName: string;
  hiredAt: string;
  dismissedAt: string | null;
  initialRating: number | null;
  notes: string | null;
}

export interface ValidateTransferResponse {
  blockers: TransferBlocker[];
  warnings: TransferBlocker[];
}

export interface ValidateUndoTransferResponse {
  canUndo: boolean;
  blockers: TransferBlocker[];
}

export interface ValidateUndoDismissResponse {
  canUndoDismiss: boolean;
  blockers: TransferBlocker[];
}

/** No `carIds`/`transferWithCars` - taxi-fleet-specific, stripped per explicit user instruction. */
export interface TransferSubjectDto {
  targetCompanyId: number;
  hiredAt: string;
  dismissedAt?: string;
  notes?: string;
  transferBalance?: boolean;
  transferRating?: boolean;
  transferSettings?: boolean;
  settingKeys?: string[];
}

export interface DismissSubjectDto {
  dismissedAt?: string;
  notes?: string;
}

export interface TransferSettingItem {
  key: string;
  value: string;
  label: string;
}

/**
 * Company-to-company employment transfer for a subject user - extracted
 * from company's DriverTransferService (same route shape, `driverId`
 * renamed `subjectId` throughout since executor-transfer.controller.ts on
 * the backend already uses the same shape for executors too). Car
 * assignment (`carIds`/`transferWithCars`/`dismissWithCars`) was removed
 * per explicit user instruction - taxi-fleet-specific, no executor/client
 * equivalent.
 */
export interface UserTransferConfig {
  getAvailableCompanies: (excludeCompanyId?: number) => Observable<ApiResponse<TransferCompanyOption[]>>;
  getHistory: (subjectId: number) => Observable<ApiResponse<TransferHistoryEntry[]>>;
  validateTransfer: (subjectId: number) => Observable<ApiResponse<ValidateTransferResponse>>;
  transfer: (subjectId: number, dto: TransferSubjectDto) => Observable<ApiResponse<unknown>>;
  dismiss: (subjectId: number, dto: DismissSubjectDto) => Observable<ApiResponse<unknown>>;
  validateUndoTransfer: (subjectId: number) => Observable<ApiResponse<ValidateUndoTransferResponse>>;
  undoTransfer: (subjectId: number) => Observable<ApiResponse<unknown>>;
  validateUndoDismiss: (subjectId: number) => Observable<ApiResponse<ValidateUndoDismissResponse>>;
  undoDismiss: (subjectId: number) => Observable<ApiResponse<unknown>>;
  /** Optional - powers the "carry settings over" checkboxes; omit to hide that section. */
  getTransferableSettings?: (subjectId: number) => Observable<TransferSettingItem[]>;
}

export const USER_TRANSFER_CONFIG = new InjectionToken<UserTransferConfig>('USER_TRANSFER_CONFIG');
